import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { profileAPI, notificationsAPI } from '../lib/api';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Switch } from '../components/ui/switch';
import { Checkbox } from '../components/ui/checkbox';
import {
  User,
  Mail,
  Building,
  Save,
  Loader2,
  AlertCircle,
  CheckCircle,
  Shield,
  Calendar,
  Bell,
  BellRing
} from 'lucide-react';
import { toast } from 'sonner';

const ORGANIZATIONS = ['USAA', 'NFAA', 'USAT', 'TFAA', 'TSAA'];

export default function Profile() {
  const { user, updateUser } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [savingNotifications, setSavingNotifications] = useState(false);
  const [profile, setProfile] = useState(null);
  const [formData, setFormData] = useState({
    full_name: '',
    organization: ''
  });
  const [notificationPrefs, setNotificationPrefs] = useState({
    email_enabled: true,
    notify_new_tournaments: true,
    notify_registered: true,
    notify_interested: true,
    notify_deadlines: true,
    watched_organizations: [],
    reminder_days: [7, 3, 1]
  });
  const [hasChanges, setHasChanges] = useState(false);
  const [hasNotificationChanges, setHasNotificationChanges] = useState(false);

  useEffect(() => {
    fetchProfile();
    fetchNotificationPrefs();
  }, []);

  const fetchProfile = async () => {
    try {
      const response = await profileAPI.get();
      setProfile(response.data);
      setFormData({
        full_name: response.data.full_name || '',
        organization: response.data.organization || ''
      });
    } catch (error) {
      console.error('Failed to fetch profile:', error);
      toast.error('Failed to load profile');
    } finally {
      setLoading(false);
    }
  };

  const fetchNotificationPrefs = async () => {
    try {
      const response = await notificationsAPI.getPreferences();
      setNotificationPrefs({
        email_enabled: response.data.email_enabled ?? true,
        notify_new_tournaments: response.data.notify_new_tournaments ?? true,
        notify_registered: response.data.notify_registered ?? true,
        notify_interested: response.data.notify_interested ?? true,
        notify_deadlines: response.data.notify_deadlines ?? true,
        watched_organizations: response.data.watched_organizations || [],
        reminder_days: response.data.reminder_days || [7, 3, 1]
      });
    } catch (error) {
      console.error('Failed to fetch notification preferences:', error);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    setHasChanges(true);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const response = await profileAPI.update(formData);
      setProfile(response.data.profile);
      setHasChanges(false);
      
      // Update auth context if name changed
      if (updateUser && response.data.profile) {
        updateUser(response.data.profile);
      }
      
      toast.success('Profile updated successfully');
    } catch (error) {
      console.error('Failed to update profile:', error);
      toast.error(error.response?.data?.detail || 'Failed to update profile');
    } finally {
      setSaving(false);
    }
  };

  const handleNotificationChange = (key, value) => {
    setNotificationPrefs(prev => ({ ...prev, [key]: value }));
    setHasNotificationChanges(true);
  };

  const handleOrgToggle = (org) => {
    setNotificationPrefs(prev => {
      const newOrgs = prev.watched_organizations.includes(org)
        ? prev.watched_organizations.filter(o => o !== org)
        : [...prev.watched_organizations, org];
      return { ...prev, watched_organizations: newOrgs };
    });
    setHasNotificationChanges(true);
  };

  const handleSaveNotifications = async () => {
    setSavingNotifications(true);
    try {
      await notificationsAPI.updatePreferences(notificationPrefs);
      setHasNotificationChanges(false);
      toast.success('Notification preferences saved');
    } catch (error) {
      console.error('Failed to save notification preferences:', error);
      toast.error('Failed to save notification preferences');
    } finally {
      setSavingNotifications(false);
    }
  };

  const getRoleBadge = (role) => {
    const colors = {
      admin: 'bg-red-100 text-red-700',
      coach: 'bg-blue-100 text-blue-700',
      archer: 'bg-green-100 text-green-700',
      parent: 'bg-purple-100 text-purple-700',
      club: 'bg-orange-100 text-orange-700',
    };
    return (
      <Badge variant="outline" className={colors[role] || 'bg-slate-100 text-slate-700'}>
        {role.charAt(0).toUpperCase() + role.slice(1)}
      </Badge>
    );
  };

  const getPlanBadge = (plan) => {
    const colors = {
      free: 'bg-slate-100 text-slate-700',
      trial: 'bg-amber-100 text-amber-700',
      pro: 'bg-blue-100 text-blue-700',
      team: 'bg-purple-100 text-purple-700',
    };
    return (
      <Badge variant="outline" className={colors[plan] || 'bg-slate-100 text-slate-700'}>
        {plan.charAt(0).toUpperCase() + plan.slice(1)} Plan
      </Badge>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="w-8 h-8 text-primary animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6 max-w-2xl">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
          <User className="w-7 h-7 text-primary" />
          My Profile
        </h1>
        <p className="text-slate-600 mt-1">
          Manage your account information and club affiliation
        </p>
      </div>

      {/* Profile Info Card */}
      <Card className="border-slate-200">
        <CardHeader>
          <CardTitle className="text-lg">Account Information</CardTitle>
          <CardDescription>
            Your basic account details
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Read-only fields */}
          <div className="space-y-4 p-4 bg-slate-50 rounded-lg">
            <div className="flex items-center gap-3">
              <Mail className="w-5 h-5 text-slate-400" />
              <div>
                <p className="text-sm text-slate-500">Email</p>
                <p className="font-medium text-slate-900">{profile?.email}</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <Shield className="w-5 h-5 text-slate-400" />
              <div>
                <p className="text-sm text-slate-500">Role & Plan</p>
                <div className="flex items-center gap-2 mt-1">
                  {getRoleBadge(profile?.role)}
                  {getPlanBadge(profile?.plan)}
                </div>
              </div>
            </div>
            {profile?.created_at && (
              <div className="flex items-center gap-3">
                <Calendar className="w-5 h-5 text-slate-400" />
                <div>
                  <p className="text-sm text-slate-500">Member Since</p>
                  <p className="font-medium text-slate-900">
                    {new Date(profile.created_at).toLocaleDateString('en-US', {
                      year: 'numeric',
                      month: 'long',
                      day: 'numeric'
                    })}
                  </p>
                </div>
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Editable Profile Card */}
      <Card className="border-slate-200">
        <CardHeader>
          <CardTitle className="text-lg">Edit Profile</CardTitle>
          <CardDescription>
            Update your name and club affiliation
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="full_name">Full Name</Label>
            <Input
              id="full_name"
              name="full_name"
              type="text"
              value={formData.full_name}
              onChange={handleChange}
              className="input-base"
              data-testid="profile-name-input"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="organization">
              Organization / Club
              {user?.role === 'coach' && (
                <span className="text-amber-600 ml-2 text-sm font-normal">
                  (Your club members will be matched by this name)
                </span>
              )}
            </Label>
            <Input
              id="organization"
              name="organization"
              type="text"
              placeholder="e.g., Dallas Archery Club"
              value={formData.organization}
              onChange={handleChange}
              className="input-base"
              data-testid="profile-org-input"
            />
            <p className="text-xs text-slate-500">
              Enter your club name exactly as registered to ensure proper coach-archer matching
            </p>
          </div>

          {hasChanges && (
            <div className="flex items-center gap-2 p-3 bg-amber-50 border border-amber-200 rounded-lg text-amber-700 text-sm">
              <AlertCircle className="w-4 h-4 flex-shrink-0" />
              <span>You have unsaved changes</span>
            </div>
          )}

          <Button
            onClick={handleSave}
            disabled={!hasChanges || saving}
            className="btn-primary"
            data-testid="profile-save-btn"
          >
            {saving ? (
              <span className="flex items-center gap-2">
                <Loader2 className="w-4 h-4 animate-spin" />
                Saving...
              </span>
            ) : (
              <span className="flex items-center gap-2">
                <Save className="w-4 h-4" />
                Save Changes
              </span>
            )}
          </Button>
        </CardContent>
      </Card>

      {/* Notification Preferences Card */}
      <Card className="border-slate-200">
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            <Bell className="w-5 h-5 text-primary" />
            Notification Preferences
          </CardTitle>
          <CardDescription>
            Manage how and when you receive notifications
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* New Tournaments Section */}
          <div className="space-y-4">
            <div className="flex items-center justify-between py-2">
              <div className="flex items-center gap-3">
                <BellRing className="w-5 h-5 text-emerald-600" />
                <div>
                  <p className="font-medium text-slate-900">New Tournament Alerts</p>
                  <p className="text-sm text-slate-500">Get notified when new tournaments are found</p>
                </div>
              </div>
              <Switch
                checked={notificationPrefs.notify_new_tournaments}
                onCheckedChange={(checked) => handleNotificationChange('notify_new_tournaments', checked)}
                data-testid="new-tournaments-toggle"
              />
            </div>

            {/* Organization Filter */}
            {notificationPrefs.notify_new_tournaments && (
              <div className="ml-8 p-4 bg-emerald-50 border border-emerald-200 rounded-lg">
                <p className="text-sm font-medium text-emerald-800 mb-3">
                  Filter by Organizations
                </p>
                <p className="text-xs text-emerald-700 mb-3">
                  Select specific organizations to watch, or leave all unchecked to receive alerts for all tournaments.
                </p>
                <div className="flex flex-wrap gap-3">
                  {ORGANIZATIONS.map((org) => (
                    <label
                      key={org}
                      className={`flex items-center gap-2 px-3 py-2 rounded-lg border cursor-pointer transition-colors ${
                        notificationPrefs.watched_organizations.includes(org)
                          ? 'bg-emerald-600 text-white border-emerald-600'
                          : 'bg-white text-slate-700 border-slate-300 hover:border-emerald-400'
                      }`}
                    >
                      <Checkbox
                        checked={notificationPrefs.watched_organizations.includes(org)}
                        onCheckedChange={() => handleOrgToggle(org)}
                        className="sr-only"
                      />
                      <span className="text-sm font-medium">{org}</span>
                    </label>
                  ))}
                </div>
                {notificationPrefs.watched_organizations.length === 0 && (
                  <p className="text-xs text-emerald-600 mt-2 italic">
                    Watching all organizations
                  </p>
                )}
              </div>
            )}
          </div>

          {/* Other Notification Types */}
          <div className="space-y-3">
            <p className="text-sm font-medium text-slate-700">Event Reminders</p>
            
            <div className="flex items-center justify-between py-2">
              <span className="text-sm text-slate-600">Registered event reminders</span>
              <Switch
                checked={notificationPrefs.notify_registered}
                onCheckedChange={(checked) => handleNotificationChange('notify_registered', checked)}
              />
            </div>
            
            <div className="flex items-center justify-between py-2">
              <span className="text-sm text-slate-600">Interested event reminders</span>
              <Switch
                checked={notificationPrefs.notify_interested}
                onCheckedChange={(checked) => handleNotificationChange('notify_interested', checked)}
              />
            </div>
            
            <div className="flex items-center justify-between py-2">
              <span className="text-sm text-slate-600">Registration deadline alerts</span>
              <Switch
                checked={notificationPrefs.notify_deadlines}
                onCheckedChange={(checked) => handleNotificationChange('notify_deadlines', checked)}
              />
            </div>
          </div>

          {/* Email Toggle */}
          <div className="space-y-3 pt-4 border-t border-slate-200">
            <p className="text-sm font-medium text-slate-700">Delivery Method</p>
            
            <div className="flex items-center justify-between py-2">
              <div className="flex items-center gap-2">
                <Mail className="w-4 h-4 text-slate-500" />
                <span className="text-sm text-slate-600">Email notifications</span>
              </div>
              <Switch
                checked={notificationPrefs.email_enabled}
                onCheckedChange={(checked) => handleNotificationChange('email_enabled', checked)}
                data-testid="email-notifications-toggle"
              />
            </div>
          </div>

          {hasNotificationChanges && (
            <div className="flex items-center gap-2 p-3 bg-amber-50 border border-amber-200 rounded-lg text-amber-700 text-sm">
              <AlertCircle className="w-4 h-4 flex-shrink-0" />
              <span>You have unsaved notification preferences</span>
            </div>
          )}

          <Button
            onClick={handleSaveNotifications}
            disabled={!hasNotificationChanges || savingNotifications}
            className="btn-primary"
            data-testid="save-notifications-btn"
          >
            {savingNotifications ? (
              <span className="flex items-center gap-2">
                <Loader2 className="w-4 h-4 animate-spin" />
                Saving...
              </span>
            ) : (
              <span className="flex items-center gap-2">
                <Save className="w-4 h-4" />
                Save Notification Preferences
              </span>
            )}
          </Button>
        </CardContent>
      </Card>
    </div>
  );
}
